import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { FormHelperText } from '@material-ui/core';
import {
  KeyboardDateTimePicker,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { useField } from '@unform/core';
import { isEqual, includes, isFunction, isNull } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';

import { canURL } from '~/helpers';

import * as S from './styles';

export default function DateTime({
  name,
  label,
  disabled,
  time,
  getValue,
  serialize,
  nullable,
  ...rest
}) {
  const inputRef = React.useRef(null);
  const permission = isEqual(canURL(), { read: true });

  const [picker, setPicker] = React.useState(nullable ? null : moment());
  const [inputError, setInputError] = React.useState(false);

  const {
    fieldName,
    defaultValue,
    registerField,
    error,
    clearError,
  } = useField(name);
  const { language } = useSelector((state) => state.user);

  const messagesKeyboard = useMemo(
    () => [
      'Formato de data inválido',
      'A data não deve ser posterior à data máxima',
      'A data não deve ser anterior à data mínima',
    ],
    []
  );

  // Modify format date and time component
  const formatDateTime = React.useMemo(() => {
    if (language === 'pt-BR') {
      if (time) return 'DD/MM/YYYY HH:mm:ss';

      return 'DD/MM/YYYY';
    }

    if (time) return 'YYYY-MM-DD HH:mm:ss';

    return 'YYYY-MM-DD';
  }, [language, time]);

  // Change Picker
  const handleChangePicker = React.useCallback((value) => {
    setPicker(value);
  }, []);

  // Toggle error component
  const handleChangeInput = React.useCallback(() => {
    if (isNull(picker) || !picker.isValid()) {
      getValue(null);
    } else {
      getValue(
        isFunction(serialize)
          ? serialize(picker)
          : picker.format(formatDateTime)
      );
    }

    inputRef.current.value = isFunction(serialize)
      ? serialize(picker)
      : picker.format(formatDateTime);
  }, [picker]);

  React.useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [fieldName, registerField]);

  React.useEffect(() => {
    if (defaultValue) {
      const convertDate = moment(defaultValue, 'DD/MM/YYYY HH:mm:ss');

      inputRef.current.value = isFunction(serialize)
        ? serialize(convertDate)
        : convertDate.format(formatDateTime);

      setPicker(convertDate);
    } else {
      inputRef.current.value = isFunction(serialize)
        ? serialize(moment().subtract(1, 'month').endOf('month'))
        : moment().format(formatDateTime);
      // inputRef.current.value = isFunction(serialize)
      //   ? serialize(moment())
      //   : moment().format(formatDateTime);
    }
  }, [defaultValue, formatDateTime]);

  React.useEffect(() => {
    if (
      picker &&
      !includes(
        ['Data inválida', 'Invalid date', 'Formato de data inválido'],
        picker.format(formatDateTime)
      )
    ) {
      handleChangeInput();
    } else {
      inputRef.current.value = null;
    }
  }, [picker]);

  React.useEffect(() => {
    if (disabled) clearError();
  }, [disabled]);

  return (
    <S.Wrapper>

      <KeyboardDatePicker
        fullWidth
        error={!!error || inputError}
        value={picker}
        onChange={handleChangePicker}
        label={label}
        inputVariant="outlined"
        size="small"
        invalidDateMessage={messagesKeyboard[0]}
        maxDateMessage={messagesKeyboard[1]}
        minDateMessage={messagesKeyboard[2]}
        {...rest}
        disabled={permission || disabled}
        // format={formatDateTime}
        format="MM/yyyy"
        onError={(err) => setInputError(includes(messagesKeyboard, err))}
        onFocus={clearError}

        openTo="month"
        views={['year', 'month']}

        minDate={moment().subtract(3, 'month').endOf('month')} // ou outro limite que você quiser
        maxDate={moment().subtract(1, 'month').endOf('month')} // impede mês atual

      />


      <input type="hidden" ref={inputRef} />
      {!!error && <FormHelperText error>{error}</FormHelperText>}
    </S.Wrapper>
  );
}

DateTime.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  time: PropTypes.bool,
  getValue: PropTypes.func,
  serialize: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  nullable: PropTypes.bool,
};

DateTime.defaultProps = {
  disabled: false,
  time: false,
  getValue: () => undefined,
  serialize: false,
  nullable: false,
};
